// Pure codeplug helpers for the Management Client: new entries, deletion with renumbering and
// reference remapping, and a client-side check that mirrors the server's validation (the server,
// server/pkg/dmrcodeplug, stays the final authority).
import type { Codeplug, CodeplugChannel, CodeplugContact, ChannelMode, RadioCodeplugState, ToneType } from '../../services/dmrCodeplug';

export const LIMITS = {
  zones: 64,
  channelsPerZone: 16,
  channels: 1024,
  contacts: 200,
  rxGroupLists: 32,
  rxGroupListSize: 16,
  name: 20,
  profileName: 64,
  profileDescription: 500,
  individualCallId: 16_776_415,
  allCallId: 16_777_215,
} as const;

// SDK manual tables 2.11.1 (CTCSS, Hz) and 2.11.2 (DCS codes).
export const CTCSS_TONES = `67.0 69.3 71.9 74.4 77.0 79.7 82.5 85.4 88.5 91.5 94.8 97.4 100.0 103.5 107.2 110.9 114.8 118.8
123.0 127.3 131.8 136.5 141.3 146.2 151.4 156.7 159.8 162.2 165.5 167.9 171.3 173.8 177.3 179.9 183.5 186.2 189.9 192.8
196.6 199.5 203.5 206.5 210.7 218.1 225.7 229.1 233.6 241.8 250.3 254.1`.split(/\s+/);
export const DCS_CODES = `023 025 026 031 032 036 043 047 051 053 054 065 071 072 073 074 114 115 116 122 125 131 132 134
143 145 152 155 156 162 165 172 174 205 212 223 225 226 243 244 245 246 251 252 255 261 263 265 266 271 274 306 311 315
325 331 332 343 346 351 356 364 365 371 411 412 413 423 431 432 445 446 452 454 455 462 464 465 466 503 506 516 523 526
532 546 565 606 612 624 627 631 632 654 662 664 703 712 723 731 732 734 743 754`.split(/\s+/);

const BANDS: Array<[number, number]> = [
  [136_000_000, 174_000_000],
  [350_000_000, 480_000_000],
];

export const emptyCodeplug = (): Codeplug => ({ schema_version: 1, contacts: [], rx_group_lists: [], channels: [], zones: [] });

/** The mode-specific fields at the SDK manual's documented defaults (DmrChannel table). The
 *  frequencies have no default: 0 is refused until the administrator enters them. */
export const modeDefaults = (mode: ChannelMode): Partial<CodeplugChannel> =>
  mode === 'DIGITAL'
    ? { color_code: 1, time_slot: 1, tx_contact: 0, rx_group_list: 0, dcdm: false }
    : { bandwidth: 'NARROW', squelch: 0, rx_tone_type: 'NONE', tx_tone_type: 'NONE', tail_elimination: false, tail_phase: 120 };

const DIGITAL_FIELDS = ['color_code', 'time_slot', 'tx_contact', 'rx_group_list', 'dcdm'] as const;
const ANALOG_FIELDS = ['bandwidth', 'squelch', 'rx_tone_type', 'rx_tone', 'tx_tone_type', 'tx_tone', 'tail_elimination', 'tail_phase'] as const;

export const newChannel = (number: number, mode: ChannelMode): CodeplugChannel => ({
  number,
  name: `Channel ${number}`,
  mode,
  rx_frequency_hz: 0,
  tx_frequency_hz: 0,
  tx_power: 'LOW',
  rx_only: false,
  tx_admit: 'ALWAYS',
  ...modeDefaults(mode),
});

/** Switches a channel's mode, dropping the other mode's fields (the server refuses mixed fields). */
export const withMode = (ch: CodeplugChannel, mode: ChannelMode): CodeplugChannel => {
  if (ch.mode === mode) return ch;
  const next: CodeplugChannel = { ...ch, mode };
  for (const f of [...DIGITAL_FIELDS, ...ANALOG_FIELDS]) delete (next as unknown as Record<string, unknown>)[f];
  return { ...next, ...modeDefaults(mode) };
};

/** Sets a tone type; NONE removes the value, another type keeps it only if valid for that type. */
export const withTone = (ch: CodeplugChannel, side: 'rx' | 'tx', type: ToneType): CodeplugChannel => {
  const next = { ...ch, [`${side}_tone_type`]: type } as CodeplugChannel;
  const key = `${side}_tone` as 'rx_tone' | 'tx_tone';
  const list = type === 'CTCSS' ? CTCSS_TONES : type === 'NONE' ? [] : DCS_CODES;
  if (!next[key] || !list.includes(next[key] as string)) {
    if (type === 'NONE') delete next[key];
    else next[key] = list[0];
  }
  return next;
};

export const newContact = (number: number): CodeplugContact => ({ number, name: `Contact ${number}`, call_type: 'GROUP', call_id: 1 });

// Maps an old 1-based number to its new number after removing `removed` (0 = removed).
const remap = (n: number, removed: number) => (n === removed ? 0 : n > removed ? n - 1 : n);
const renumber = <T extends { number: number }>(list: T[]): T[] => list.map((x, i) => ({ ...x, number: i + 1 }));

/** Removes a channel; zones lose the reference and higher numbers move down by one. */
export const deleteChannel = (c: Codeplug, number: number): Codeplug => ({
  ...c,
  channels: renumber(c.channels.filter((x) => x.number !== number)),
  zones: c.zones.map((z) => ({ ...z, channels: z.channels.map((n) => remap(n, number)).filter((n) => n > 0) })),
});

/** Removes a contact; a channel's TX contact becomes none (0), RX group lists drop it. */
export const deleteContact = (c: Codeplug, number: number): Codeplug => ({
  ...c,
  contacts: renumber(c.contacts.filter((x) => x.number !== number)),
  rx_group_lists: c.rx_group_lists.map((l) => ({ ...l, contacts: l.contacts.map((n) => remap(n, number)).filter((n) => n > 0) })),
  channels: c.channels.map((ch) => (ch.mode === 'DIGITAL' && ch.tx_contact !== undefined ? { ...ch, tx_contact: remap(ch.tx_contact, number) } : ch)),
});

/** Removes an RX group list; channels using it get none (0). */
export const deleteRxGroupList = (c: Codeplug, number: number): Codeplug => ({
  ...c,
  rx_group_lists: renumber(c.rx_group_lists.filter((x) => x.number !== number)),
  channels: c.channels.map((ch) => (ch.mode === 'DIGITAL' && ch.rx_group_list !== undefined ? { ...ch, rx_group_list: remap(ch.rx_group_list, number) } : ch)),
});

export const deleteZone = (c: Codeplug, number: number): Codeplug => ({ ...c, zones: renumber(c.zones.filter((x) => x.number !== number)) });

/** Moves a zone one place up/down (zones have no references to remap). */
export const moveZone = (c: Codeplug, number: number, delta: -1 | 1): Codeplug => {
  const i = number - 1;
  const j = i + delta;
  if (j < 0 || j >= c.zones.length) return c;
  const zones = [...c.zones];
  [zones[i], zones[j]] = [zones[j], zones[i]];
  return { ...c, zones: renumber(zones) };
};

const nameProblem = (name: string, max: number = LIMITS.name): string | null => {
  const t = name.trim();
  if (!t) return 'is required';
  if ([...t].length > max) return `is longer than ${max} characters`;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f]/.test(t)) return 'contains control characters';
  return null;
};

export const inBand = (hz: number) => Number.isInteger(hz) && BANDS.some(([lo, hi]) => hz >= lo && hz <= hi);

/** Client-side mirror of the server validation; returns readable problems (empty = looks valid). */
export const codeplugProblems = (c: Codeplug): string[] => {
  const p: string[] = [];
  const push = (path: string, msg: string) => p.push(`${path}: ${msg}`);
  if (c.channels.length === 0) push('Channels', 'at least one channel is required');
  if (c.channels.length > LIMITS.channels) push('Channels', `at most ${LIMITS.channels}`);
  if (c.zones.length === 0) push('Zones', 'at least one zone is required');
  if (c.zones.length > LIMITS.zones) push('Zones', `at most ${LIMITS.zones}`);
  if (c.contacts.length > LIMITS.contacts) push('Contacts', `at most ${LIMITS.contacts}`);
  if (c.rx_group_lists.length > LIMITS.rxGroupLists) push('RX group lists', `at most ${LIMITS.rxGroupLists}`);

  const types = new Map(c.contacts.map((x) => [x.number, x.call_type]));
  c.contacts.forEach((x, i) => {
    const at = `Contact ${i + 1}`;
    if (x.number !== i + 1) push(at, `number must be ${i + 1}`);
    const n = nameProblem(x.name);
    if (n) push(at, `name ${n}`);
    if (x.call_type === 'ALL') {
      if (x.call_id !== LIMITS.allCallId) push(at, `an All call contact has the fixed ID ${LIMITS.allCallId}`);
    } else if (!Number.isInteger(x.call_id) || x.call_id < 1 || x.call_id > LIMITS.individualCallId) {
      push(at, `call ID must be 1-${LIMITS.individualCallId}`);
    }
  });
  c.rx_group_lists.forEach((l, i) => {
    const at = `RX group list ${i + 1}`;
    if (l.number !== i + 1) push(at, `number must be ${i + 1}`);
    const n = nameProblem(l.name);
    if (n) push(at, `name ${n}`);
    if (l.contacts.length === 0 || l.contacts.length > LIMITS.rxGroupListSize) push(at, `must list 1-${LIMITS.rxGroupListSize} group contacts`);
    if (new Set(l.contacts).size !== l.contacts.length) push(at, 'lists a contact twice');
    l.contacts.forEach((k) => types.get(k) !== 'GROUP' && push(at, `contact ${k} is not a group contact`));
  });
  c.channels.forEach((ch, i) => {
    const at = `Channel ${i + 1}`;
    if (ch.number !== i + 1) push(at, `number must be ${i + 1}`);
    const n = nameProblem(ch.name);
    if (n) push(at, `name ${n}`);
    if (!inBand(ch.rx_frequency_hz)) push(at, 'RX frequency must be 136-174 or 350-480 MHz');
    if (!inBand(ch.tx_frequency_hz)) push(at, 'TX frequency must be 136-174 or 350-480 MHz');
    if (ch.mode === 'DIGITAL') {
      if (ch.color_code === undefined || ch.color_code < 0 || ch.color_code > 15) push(at, 'color code must be 0-15');
      if (ch.time_slot !== 1 && ch.time_slot !== 2) push(at, 'time slot must be 1 or 2');
      if (ch.tx_contact === undefined || (ch.tx_contact !== 0 && !types.has(ch.tx_contact))) push(at, 'TX contact does not exist');
      if (ch.rx_group_list === undefined || ch.rx_group_list < 0 || ch.rx_group_list > c.rx_group_lists.length) push(at, 'RX group list does not exist');
    } else {
      if (ch.squelch === undefined || ch.squelch < 0 || ch.squelch > 9) push(at, 'squelch must be 0-9');
      for (const side of ['rx', 'tx'] as const) {
        const type = ch[`${side}_tone_type`];
        const value = ch[`${side}_tone`];
        if (type === 'CTCSS' && !CTCSS_TONES.includes(value ?? '')) push(at, `${side.toUpperCase()} CTCSS tone is not in the SDK table`);
        if ((type === 'DCS' || type === 'DCS_INVERTED') && !DCS_CODES.includes(value ?? '')) push(at, `${side.toUpperCase()} DCS code is not in the SDK table`);
      }
    }
  });
  c.zones.forEach((z, i) => {
    const at = `Zone ${i + 1}`;
    if (z.number !== i + 1) push(at, `number must be ${i + 1}`);
    const n = nameProblem(z.name);
    if (n) push(at, `name ${n}`);
    if (z.channels.length === 0 || z.channels.length > LIMITS.channelsPerZone) push(at, `must list 1-${LIMITS.channelsPerZone} channels`);
    if (new Set(z.channels).size !== z.channels.length) push(at, 'lists a channel twice');
    z.channels.forEach((k) => (k < 1 || k > c.channels.length) && push(at, `channel ${k} does not exist`));
  });
  return p;
};

export const profileProblems = (name: string, description: string): string[] => {
  const p: string[] = [];
  const n = nameProblem(name, LIMITS.profileName);
  if (n) p.push(`Profile name: ${n}`);
  if ([...description.trim()].length > LIMITS.profileDescription) p.push(`Description: at most ${LIMITS.profileDescription} characters`);
  return p;
};

/** The overwrite confirmation of an explicit profile load into one radio. */
export const loadConfirmText = (profileName: string, state: RadioCodeplugState): string =>
  `Load codeplug profile "${profileName}" into this radio now?\n\n` +
  (state.assigned
    ? `This REPLACES the radio's whole DMR codeplug${state.locally_modified ? ', including all edits made for this radio only' : ''}. It cannot be undone.`
    : 'The server holds no managed codeplug for this radio yet.') +
  '\n\nLater changes to the profile are NOT copied to the radio automatically.';

/**
 * Whether the radio has confirmed the stored codeplug: only its own apply report for exactly the
 * current revision says so. Until then the stored copy is only queued for the radio.
 */
export const applyStatus = (state: RadioCodeplugState): 'none' | 'queued' | 'applied' | 'failed' => {
  if (!state.assigned) return 'none';
  const report = state.last_apply_report;
  if (!report || report.revision !== state.revision) return 'queued';
  return report.result === 'APPLIED' ? 'applied' : 'failed';
};

/** Identity of one radio DMR settings view: a different radio or network is a different view. */
export const dmrSettingsKey = (radioId: string, orgId: string): string => `${radioId}/${orgId}`;

/** The profile to preselect: the current choice, else the radio's source, else the first - each only while it is listed. */
export const pickProfile = (current: string, sourceProfileId: string | null, profiles: Array<{ id: string }>): string =>
  [current, sourceProfileId, profiles[0]?.id].find((id) => id && profiles.some((p) => p.id === id)) ?? '';

export const mhz =(hz: number): string => (hz ? (hz / 1e6).toFixed(6).replace(/0{1,3}$/, '') : '');
export const hzFromMhz = (text: string): number => {
  const v = Number(text.replace(',', '.'));
  return Number.isFinite(v) ? Math.round(v * 1e6) : 0;
};
